import { useState } from 'react';
import styled from '@emotion/styled';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import SheetDialog, { SheetDialogActions, SheetDialogButton, SheetDialogControls } from '@/components/common/elements/SheetDialog';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import { REPORT_REASONS } from './reportReasons';

/**
 * 신고 사유 고르기 창 — 글·댓글 신고(ReportBlockMenu)와 대화방 신고(ThreadReportDialog) 공용(2026-10-11 D2).
 * 폰은 차단 확인과 같은 아래 시트, PC 는 가운데 작은 창(공용 SheetDialog — 키보드·뒤로가기·바깥 누르기 처리 포함).
 * 오류 빨강은 FORM_COLOR.danger 하나, "기타" 칸 글자 16px(아이폰 확대 방지).
 */
export interface ReportReasonSheetProps {
    open: boolean;
    title?: string;
    /** 사유 목록 위 안내 */
    description: string;
    /** 메시지 신고면 그 메시지 본문 */
    quote?: string | null;
    onClose: () => void;
    /** 보내기 — 성공하면 true(창을 닫는다) */
    onSubmit: (reason: string, detail?: string) => Promise<boolean>;
    /** 몸통 스크롤 잠금을 끈다 — 위 바·입력줄이 sticky 인 대화방(SheetDialog 설명 참고) */
    disableScrollLock?: boolean;
}

const Quote = styled.p`
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${FORM_COLOR.lineSoft};
    font-size: 14px;
    line-height: 1.5;
    color: ${FORM_COLOR.sub};
    overflow-wrap: anywhere;
`;

const Guide = styled.p`
    margin: 0 0 8px;
    font-size: 13px;
    line-height: 1.6;
    color: ${FORM_COLOR.muted};
    word-break: keep-all;
    /* 마지막 줄에 "다." 한 글자만 남지 않게 */
    text-wrap: pretty;
`;

const ErrorText = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${FORM_COLOR.danger};
`;

const DetailInput = styled.textarea`
    display: block;
    box-sizing: border-box;
    width: 100%;
    min-height: 84px;
    margin-top: 8px;
    padding: 12px 14px;
    border: 1px solid ${FORM_COLOR.border};
    border-radius: 12px;
    background: #fff;
    font-family: inherit;
    /* 아이폰은 16px 미만 칸을 누르면 화면을 확대한다(UI 가이드 7.1) */
    font-size: 16px;
    line-height: 1.5;
    color: ${FORM_COLOR.text};
    resize: vertical;

    &::placeholder {
        color: ${FORM_COLOR.placeholder};
    }
    &:focus {
        outline: none;
        border-color: ${FORM_COLOR.primary};
    }
`;

const ReasonRadio = (
    <Radio size="small" sx={{ '&.Mui-checked': { color: FORM_COLOR.primary } }} />
);

const ReportReasonSheetBody = ({ title = '신고하기', description, quote, onClose, onSubmit, disableScrollLock = false }: Omit<ReportReasonSheetProps, 'open'>) => {
    const [reason, setReason] = useState<string>('');
    const [detail, setDetail] = useState<string>('');
    const [reasonError, setReasonError] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const submit = async (controls: SheetDialogControls): Promise<void> => {
        if (!reason) {
            setReasonError(true);
            return;
        }
        setSubmitting(true);
        let ok: boolean = false;
        try {
            ok = await onSubmit(reason, reason === 'OTHER' ? detail.trim() || undefined : undefined);
        } finally {
            setSubmitting(false);
        }
        if (ok) controls.close();
    };

    return (
        <SheetDialog
            open
            title={title}
            onClose={onClose}
            busy={submitting}
            pcWidth={420}
            disableScrollLock={disableScrollLock}
            footer={(controls: SheetDialogControls) => (
                <SheetDialogActions>
                    <SheetDialogButton type="button" onClick={controls.close} disabled={submitting}>취소</SheetDialogButton>
                    <SheetDialogButton type="button" tone="primary" onClick={() => void submit(controls)} disabled={submitting}>신고 접수</SheetDialogButton>
                </SheetDialogActions>
            )}
        >
            {quote && <Quote>{quote}</Quote>}
            <Guide>{description}</Guide>
            <RadioGroup
                value={reason}
                onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError(false);
                }}
            >
                {REPORT_REASONS.map((r) => (
                    <FormControlLabel
                        key={r.value}
                        value={r.value}
                        control={ReasonRadio}
                        label={r.label}
                        sx={{ minHeight: 44, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 15, color: FORM_COLOR.text } }}
                    />
                ))}
            </RadioGroup>
            {reasonError && <ErrorText role="alert">신고 사유를 선택해 주세요.</ErrorText>}
            {reason === 'OTHER' && (
                <DetailInput
                    rows={3}
                    placeholder="사유를 입력해 주세요 (선택)"
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    maxLength={500}
                    aria-label="기타 신고 사유"
                />
            )}
        </SheetDialog>
    );
};

/** 열려 있을 때만 그린다 — 닫으면 고른 사유·글이 처음으로 돌아간다 */
const ReportReasonSheet = ({ open, ...rest }: ReportReasonSheetProps) => (open ? <ReportReasonSheetBody {...rest} /> : null);

export default ReportReasonSheet;
